import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormCheckbox } from 'ui/components/elements/FormCheckbox/FormCheckbox';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { CustomLink } from 'ui/components/elements/CustomLink/CustomLink';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { useSendRegistrationMail } from './useSendRegistrationMail';
import { TopHeader } from 'ui/components/panes/Header/TopHeader';
import { Terms } from 'ui/components/features/Terms/Terms';
import { mqMin } from 'utils/responsive';

const BgWrapper = styled.div`
    background-color: var(--pale-blue);
    min-height: calc(100vh - var(--height-top-header));

    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 16px;
`;

const Wrapper = styled.div`
    margin: 20px auto;
    padding: 40px 30px;
    border-radius: 12px;
    background-color: var(--white);
    max-width: 1000px;
    height: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-size: var(--font-medium);
    text-align: center;

    h4 {
        color: var(--dark-blue);
    }

    ${mqMin('SP')} {
        margin: 0 auto;
        width: 100%;
        padding: 30px 16px;
        font-size: 0.9rem;
        gap: 16px;
    }
`;

const Title = styled.div`
    font-size: var(--font-xxx-large);
    color: var(--main-color-blue);
    font-weight: 600;
    margin-bottom: 20px;
    text-align: center;
`;

const ButtonContainer = styled.div`
    margin: 20px auto 0;

    button {
        width: 100%;

        ${mqMin('SP')} {
            width: 400px;
        }
    }
`;

const CheckboxGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin: 10px auto;
    max-width: 400px;
`;

export const SendRegistrationMail: React.FC = () => {
    const { control, loading, errors, sendMail } = useSendRegistrationMail();

    return (
        <>
            <TopHeader />
            <BgWrapper>
                <Wrapper>
                    <Title>売り手企業 無料会員登録</Title>
                    <h4>メールアドレスを入力してください</h4>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <div
                                style={{
                                    maxWidth: '400px',
                                    width: '100%',
                                    margin: '0 auto',
                                }}
                            >
                                <FormInput
                                    {...field}
                                    placeholder="example@example.jp"
                                    hasError={!!errors.email?.message}
                                />
                                {errors.email?.message && <FormError message={errors.email.message} />}
                            </div>
                        )}
                    />
                    <Terms />
                    <CheckboxGroup>
                        <Controller
                            name="agreed"
                            control={control}
                            render={({ field }) => (
                                <FormCheckbox
                                    id="agreed"
                                    checked={field.value}
                                    onChange={() => field.onChange(!field.value)}
                                    disableBorder
                                    hasError={!!errors.agreed?.message}
                                >
                                    利用規約に同意する
                                </FormCheckbox>
                            )}
                        />
                        <Controller
                            name="privacyAgreed"
                            control={control}
                            render={({ field }) => (
                                <FormCheckbox
                                    id="privacyAgreed"
                                    checked={field.value}
                                    onChange={() => field.onChange(!field.value)}
                                    disableBorder
                                    hasError={!!errors.privacyAgreed?.message}
                                >
                                    <CustomLink
                                        inline
                                        textDecoration="underline"
                                        url="/privacy/"
                                        text="プライバシーポリシー"
                                        target="_blank"
                                    />
                                    に同意する
                                </FormCheckbox>
                            )}
                        />
                        <Controller
                            name="cookieAgreed"
                            control={control}
                            render={({ field }) => (
                                <FormCheckbox
                                    id="cookieAgreed"
                                    checked={field.value}
                                    onChange={() => field.onChange(!field.value)}
                                    disableBorder
                                    hasError={!!errors.cookieAgreed?.message}
                                >
                                    Cookie利用に同意する
                                </FormCheckbox>
                            )}
                        />
                        {(errors.agreed?.message || errors.privacyAgreed?.message || errors.cookieAgreed?.message) && (
                            <FormError message="すべての項目に同意してください" />
                        )}
                    </CheckboxGroup>
                    <ButtonContainer>
                        <ActionButton
                            disabled={loading}
                            onClick={sendMail}
                            bgColor="main-color-blue"
                            style={{ maxWidth: '400px' }}
                        >
                            登録する（無料）
                        </ActionButton>
                    </ButtonContainer>
                </Wrapper>
            </BgWrapper>
        </>
    );
};
